import {
  CAMERA_PATH_VERSION,
  MAX_PATH_DURATION_MS,
  applyLookKeys,
  normalizePathLook,
  pathSampleTimes,
  type CameraPath,
  type CameraPathAuthoring,
  type PathLook,
  type PathPoint,
  type PathVec3,
} from "@/lib/location-scene-3d/camera-path"
import {
  clockAtArcFraction,
  defaultFlySpeedProfile,
  normalizeSpeedProfile,
  pathProgress,
  type CameraSpeedProfile,
} from "@/lib/location-scene-3d/camera-speed"

export const DEFAULT_SPLINE_DURATION_MS = 8_000
export const MIN_SPLINE_DURATION_MS = 500
export const MAX_PATH_POINTS = 64
/** Отклонение меньше радиуса трубы не становится точкой. На см-сцене умножается на chrome. */
export const RECORDED_FIT_EPSILON_M = 0.05
const MAX_TANGENT_LEN = 80
const CURVE_STEPS = 16

export type CurveSample = {
  pos: PathVec3
  dir: PathVec3
  segment: number
}

function clamp(v: number, a: number, b: number) {
  return Math.min(b, Math.max(a, v))
}

function isFiniteNum(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n)
}

export function vecLen(v: PathVec3): number {
  return Math.hypot(v[0], v[1], v[2])
}

function sub(a: PathVec3, b: PathVec3): PathVec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

function add(a: PathVec3, b: PathVec3): PathVec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
}

function scale(v: PathVec3, s: number): PathVec3 {
  return [v[0] * s, v[1] * s, v[2] * s]
}

function dot(a: PathVec3, b: PathVec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

export function clampTangent(v: PathVec3): PathVec3 {
  const len = vecLen(v)
  if (len <= MAX_TANGENT_LEN || len < 1e-8) return v
  return scale(v, MAX_TANGENT_LEN / len)
}

function asVec3(raw: unknown): PathVec3 | null {
  if (!Array.isArray(raw) || raw.length !== 3) return null
  if (!isFiniteNum(raw[0]) || !isFiniteNum(raw[1]) || !isFiniteNum(raw[2])) return null
  return [raw[0], raw[1], raw[2]]
}

function normalizeLook(raw: unknown): PathLook {
  return normalizePathLook(raw)
}

export function normalizePathAuthoring(raw: unknown): CameraPathAuthoring | null {
  if (!raw || typeof raw !== "object") return null
  const body = raw as { smooth?: unknown; look?: unknown; points?: unknown }
  if (!Array.isArray(body.points)) return null
  const points: PathPoint[] = []
  for (const item of body.points) {
    if (points.length >= MAX_PATH_POINTS) break
    if (!item || typeof item !== "object") continue
    const pos = asVec3((item as { pos?: unknown }).pos)
    if (!pos) continue
    const tangentRaw = (item as { tangent?: unknown }).tangent
    const tangent = tangentRaw == null ? null : asVec3(tangentRaw)
    points.push({ pos, tangent: tangent ? clampTangent(tangent) : null })
  }
  if (points.length === 0) return null
  return {
    smooth: body.smooth !== false,
    look: normalizeLook(body.look),
    points,
  }
}

export function clampSplineDuration(ms: number): number {
  if (!Number.isFinite(ms)) return DEFAULT_SPLINE_DURATION_MS
  return clamp(Math.round(ms), MIN_SPLINE_DURATION_MS, MAX_PATH_DURATION_MS)
}

/** Первая точка — высота глаз, следующие — высота предыдущей. */
export function drawPointPosition(points: readonly PathPoint[], eyeY: number, hit: { x: number; z: number }): PathVec3 {
  const y = points.length === 0 ? eyeY : points[points.length - 1].pos[1]
  return [hit.x, y, hit.z]
}

const PATH_DRAW_FALLBACK_Y = 5

/** Высота точки: запасная, если точек ещё нет, иначе высота последней. */
export function drawPlaneY(points: readonly PathPoint[], fallbackY = PATH_DRAW_FALLBACK_Y): number {
  if (points.length === 0) return fallbackY
  return points[points.length - 1].pos[1]
}

/** XZ — луч и пол y=0. Y — высота точки. Стены и объекты не участвуют. */
export function drawPointFromClick(
  points: readonly PathPoint[],
  origin: PathVec3,
  direction: PathVec3,
  fallbackY = PATH_DRAW_FALLBACK_Y,
): PathVec3 | null {
  const y = drawPlaneY(points, fallbackY)
  const hit = rayHorizontalPlaneHit(origin, direction, 0)
  if (!hit) return null
  return [hit.x, y, hit.z]
}

/** Луч и горизонтальная плоскость. `t < 0` — плоскость сзади. */
export function rayHorizontalPlaneHit(
  origin: PathVec3,
  direction: PathVec3,
  planeY: number,
): { x: number; z: number } | null {
  if (Math.abs(direction[1]) < 1e-8) return null
  const t = (planeY - origin[1]) / direction[1]
  if (t < -1e-4) return null
  return {
    x: origin[0] + direction[0] * t,
    z: origin[2] + direction[2] * t,
  }
}

/** Смещение Безье. Входная ручка — минус выходная. */
function autoTangent(points: readonly PathPoint[], index: number): PathVec3 {
  const n = points.length
  if (n < 2) return [0, 0, 0]
  if (index <= 0) return scale(sub(points[1].pos, points[0].pos), 1 / 3)
  if (index >= n - 1) return scale(sub(points[n - 1].pos, points[n - 2].pos), 1 / 3)
  return scale(sub(points[index + 1].pos, points[index - 1].pos), 1 / 6)
}

export function displayedTangent(authoring: CameraPathAuthoring, index: number): PathVec3 {
  return tangentAt(authoring.points, index, authoring.smooth)
}

function tangentAt(points: readonly PathPoint[], index: number, smooth: boolean): PathVec3 {
  if (!smooth) {
    if (points.length < 2) return [0, 0, 0]
    if (index <= 0) return sub(points[1].pos, points[0].pos)
    if (index >= points.length - 1) return sub(points[index].pos, points[index - 1].pos)
    return sub(points[index + 1].pos, points[index].pos)
  }
  const custom = points[index]?.tangent
  if (custom) return custom
  return autoTangent(points, index)
}

function bezier(a: PathVec3, c1: PathVec3, c2: PathVec3, b: PathVec3, t: number): PathVec3 {
  const u = 1 - t
  const uu = u * u
  const tt = t * t
  return add(add(scale(a, uu * u), scale(c1, 3 * uu * t)), add(scale(c2, 3 * u * tt), scale(b, tt * t)))
}

function bezierDir(a: PathVec3, c1: PathVec3, c2: PathVec3, b: PathVec3, t: number): PathVec3 {
  const u = 1 - t
  return add(add(scale(sub(c1, a), 3 * u * u), scale(sub(c2, c1), 6 * u * t)), scale(sub(b, c2), 3 * t * t))
}

function evalSegment(
  points: readonly PathPoint[],
  segment: number,
  t: number,
  smooth: boolean,
): { pos: PathVec3; dir: PathVec3 } {
  const a = points[segment].pos
  const b = points[segment + 1].pos
  if (!smooth) {
    const dir = sub(b, a)
    return {
      pos: [a[0] + dir[0] * t, a[1] + dir[1] * t, a[2] + dir[2] * t],
      dir,
    }
  }
  const outA = tangentAt(points, segment, true)
  const inB = scale(tangentAt(points, segment + 1, true), -1)
  const c1 = add(a, outA)
  const c2 = add(b, inB)
  return { pos: bezier(a, c1, c2, b, t), dir: bezierDir(a, c1, c2, b, t) }
}

export function curvePolyline(authoring: CameraPathAuthoring, stepsPerSegment = CURVE_STEPS): CurveSample[] {
  const points = authoring.points
  if (points.length === 0) return []
  if (points.length === 1) return [{ pos: points[0].pos, dir: [0, 0, -1], segment: 0 }]
  const steps = Math.max(1, stepsPerSegment)
  const out: CurveSample[] = []
  for (let segment = 0; segment < points.length - 1; segment++) {
    for (let step = 0; step <= steps; step++) {
      if (segment > 0 && step === 0) continue
      const t = step / steps
      const sample = evalSegment(points, segment, t, authoring.smooth)
      out.push({ pos: sample.pos, dir: sample.dir, segment })
    }
  }
  return out
}

type ArcSample = CurveSample & { dist: number }

function arcTable(authoring: CameraPathAuthoring): ArcSample[] {
  const poly = curvePolyline(authoring)
  let dist = 0
  return poly.map((sample, index) => {
    if (index > 0) dist += vecLen(sub(sample.pos, poly[index - 1].pos))
    return { ...sample, dist }
  })
}

function blendDir(a: PathVec3, b: PathVec3, u: number): PathVec3 {
  const mixed: PathVec3 = [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, a[2] + (b[2] - a[2]) * u]
  const len = vecLen(mixed)
  if (len > 1e-8) return scale(mixed, 1 / len)
  const fallback = vecLen(a) > 1e-8 ? a : b
  const fallbackLen = vecLen(fallback)
  if (fallbackLen < 1e-8) return [0, 0, -1]
  return scale(fallback, 1 / fallbackLen)
}

function sampleAtDistance(table: readonly ArcSample[], distance: number): { pos: PathVec3; dir: PathVec3 } {
  if (table.length === 0) return { pos: [0, 0, 0], dir: [0, 0, -1] }
  if (distance <= 0) return table[0]
  const last = table[table.length - 1]
  if (distance >= last.dist) return last
  let lo = 0
  let hi = table.length - 1
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1
    if (table[mid].dist < distance) lo = mid
    else hi = mid
  }
  const a = table[lo]
  const b = table[hi]
  const span = b.dist - a.dist
  const u = span <= 1e-8 ? 0 : (distance - a.dist) / span
  return {
    pos: add(a.pos, scale(sub(b.pos, a.pos), u)),
    dir: blendDir(a.dir, b.dir, u),
  }
}

/** Точка на той же дуге, что рисует линия. 0 — начало пути, 1 — конец. */
export function authoredPositionAt(
  authoring: CameraPathAuthoring,
  fraction: number,
): { pos: PathVec3; dir: PathVec3 } | null {
  if (authoring.points.length < 2) return null
  const table = arcTable(authoring)
  const total = table[table.length - 1]?.dist ?? 0
  const share = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0
  return sampleAtDistance(table, total <= 1e-8 ? 0 : share * total)
}

/** Взгляд камеры: yaw вокруг Y, pitch вокруг X, порядок YXZ, вперёд −Z. */
export function directionFromYawPitch(yaw: number, pitch: number): PathVec3 {
  const cp = Math.cos(pitch)
  return [-Math.sin(yaw) * cp, Math.sin(pitch), -Math.cos(yaw) * cp]
}

export function yawPitchFromDirection(dir: PathVec3): { yaw: number; pitch: number } {
  const len = vecLen(dir)
  if (len < 1e-8) return { yaw: 0, pitch: 0 }
  const x = dir[0] / len
  const y = dir[1] / len
  const z = dir[2] / len
  const pitch = Math.asin(clamp(y, -1, 1))
  const yaw = Math.atan2(-x, -z)
  return { yaw, pitch }
}

export function lookYawPitch(
  pos: PathVec3,
  tangent: PathVec3,
  look: PathLook,
  target: PathVec3 | null,
): { yaw: number; pitch: number } {
  if (look.mode === "observe" && target) {
    const toTarget = sub(target, pos)
    if (vecLen(toTarget) > 1e-4) return yawPitchFromDirection(toTarget)
  }
  if (vecLen(tangent) < 1e-8) return { yaw: 0, pitch: 0 }
  return yawPitchFromDirection(tangent)
}

export function bakeSplinePath(
  authoring: CameraPathAuthoring,
  opts: { durationMs: number; fov: number; speed?: CameraSpeedProfile | null },
): CameraPath {
  const duration = clampSplineDuration(opts.durationMs)
  const fov = Number.isFinite(opts.fov) ? opts.fov : 55
  const clean = normalizePathAuthoring(authoring) ?? authoring
  const speed = opts.speed ? normalizeSpeedProfile(opts.speed, duration) : null
  if (clean.points.length < 2) {
    return {
      version: CAMERA_PATH_VERSION,
      duration_ms: duration,
      samples: [],
      authoring: clean,
      ...(speed ? { speed } : {}),
    }
  }
  const table = arcTable(clean)
  const total = table[table.length - 1]?.dist ?? 0
  const samples: CameraPath["samples"] = pathSampleTimes(duration).map((t) => {
    const fraction = speed ? pathProgress(speed, duration, t) : duration <= 0 ? 0 : t / duration
    const distance = total <= 1e-8 ? 0 : fraction * total
    const at = sampleAtDistance(table, distance)
    const yp = lookYawPitch(at.pos, at.dir, clean.look, null)
    return { t, pos: at.pos, yaw: yp.yaw, pitch: yp.pitch, fov }
  })
  return {
    version: CAMERA_PATH_VERSION,
    duration_ms: duration,
    samples,
    authoring: clean,
    ...(speed ? { speed } : {}),
  }
}

function pointSegmentDistance(point: PathVec3, a: PathVec3, b: PathVec3): number {
  const ab = sub(b, a)
  const lenSq = dot(ab, ab)
  if (lenSq < 1e-12) return vecLen(sub(point, a))
  const t = clamp(dot(sub(point, a), ab) / lenSq, 0, 1)
  return vecLen(sub(point, add(a, scale(ab, t))))
}

const HANDLE_PASSES = 4

/** Сколько шагов кривой на участок, чтобы хорда полилинии не съела допуск. */
function curveStepsForSpan(from: PathVec3, to: PathVec3, epsilon: number): number {
  const chord = vecLen(sub(to, from))
  const target = Math.max(epsilon * 0.25, 1e-6)
  return clamp(Math.ceil(chord / target), 8, 48)
}

function unitOr(v: PathVec3, fallback: PathVec3): PathVec3 {
  const len = vecLen(v)
  if (len < 1e-8) return fallback
  return scale(v, 1 / len)
}

/** Направление хода в кадре: разность соседних кадров. */
function recordedTravelDir(positions: readonly PathVec3[], index: number): PathVec3 {
  const prev = positions[Math.max(0, index - 1)]
  const next = positions[Math.min(positions.length - 1, index + 1)]
  const across = sub(next, prev)
  if (vecLen(across) >= 1e-8) return unitOr(across, [1, 0, 0])
  if (index + 1 < positions.length) return unitOr(sub(positions[index + 1], positions[index]), [1, 0, 0])
  if (index > 0) return unitOr(sub(positions[index], positions[index - 1]), [1, 0, 0])
  return [1, 0, 0]
}

/** Вдоль записи. Если против отрезка к соседней оставленной точке — берём этот отрезок. */
function handleDirection(positions: readonly PathVec3[], indices: readonly number[], at: number): PathVec3 {
  const index = indices[at]
  const recorded = recordedTravelDir(positions, index)
  const outgoing = at + 1 < indices.length ? sub(positions[indices[at + 1]], positions[index]) : null
  const incoming = at > 0 ? sub(positions[index], positions[indices[at - 1]]) : null
  const along = outgoing && vecLen(outgoing) >= 1e-8 ? outgoing : incoming
  if (along && vecLen(along) >= 1e-8 && dot(recorded, along) <= 0) return unitOr(along, recorded)
  return recorded
}

function maxHandleLength(positions: readonly PathVec3[], indices: readonly number[], at: number): number {
  const index = indices[at]
  let cap = MAX_TANGENT_LEN
  if (at > 0) cap = Math.min(cap, vecLen(sub(positions[index], positions[indices[at - 1]])))
  if (at + 1 < indices.length) cap = Math.min(cap, vecLen(sub(positions[indices[at + 1]], positions[index])))
  return Math.max(0, cap)
}

function pointsWithLengths(
  positions: readonly PathVec3[],
  indices: readonly number[],
  dirs: readonly PathVec3[],
  lengths: readonly number[],
): PathPoint[] {
  return indices.map((index, at) => ({
    pos: [positions[index][0], positions[index][1], positions[index][2]] as PathVec3,
    tangent: clampTangent(scale(dirs[at], lengths[at])),
  }))
}

type SpanDeviation = { error: number; index: number }

function segmentPolyline(points: readonly PathPoint[], segment: number, epsilon: number): PathVec3[] {
  const steps = curveStepsForSpan(points[segment].pos, points[segment + 1].pos, epsilon)
  const span: PathVec3[] = []
  for (let step = 0; step <= steps; step += 1) {
    span.push(evalSegment(points, segment, step / steps, true).pos)
  }
  return span
}

function distanceToSpan(pos: PathVec3, span: readonly PathVec3[]): number {
  let best = Infinity
  for (let i = 0; i < span.length - 1; i += 1) {
    const dist = pointSegmentDistance(pos, span[i], span[i + 1])
    if (dist < best) best = dist
  }
  return best
}

/** Худший кадр и сумма квадратов отклонений на одном участке. */
function segmentDeviation(
  positions: readonly PathVec3[],
  points: readonly PathPoint[],
  from: number,
  to: number,
  segment: number,
  epsilon: number,
): SpanDeviation & { energy: number } {
  const span = segmentPolyline(points, segment, epsilon)
  let error = 0
  let energy = 0
  let index = from
  for (let sample = from + 1; sample < to; sample += 1) {
    const dist = distanceToSpan(positions[sample], span)
    energy += dist * dist
    if (dist > error) {
      error = dist
      index = sample
    }
  }
  return { error, index, energy }
}

function worstDeviation(parts: readonly SpanDeviation[]): SpanDeviation {
  let error = 0
  let index = 0
  for (const part of parts) {
    if (part.error > error) {
      error = part.error
      index = part.index
    }
  }
  return { error, index }
}

/**
 * Длина каждой ручки — скаляр вдоль направления записи, не длиннее соседнего отрезка.
 * Подбор уменьшает отклонение кадров на соседних участках.
 */
function fitHandleLengths(
  positions: readonly PathVec3[],
  indices: readonly number[],
  epsilon: number,
): { points: PathPoint[]; deviation: SpanDeviation } {
  const dirs = indices.map((_, at) => handleDirection(positions, indices, at))
  const caps = indices.map((_, at) => maxHandleLength(positions, indices, at))
  const lengths = caps.map((cap) => Math.min(cap, cap / 3))
  const segments = indices.length - 1
  const refresh = (built: readonly PathPoint[]) =>
    Array.from({ length: segments }, (_, segment) =>
      segmentDeviation(positions, built, indices[segment], indices[segment + 1], segment, epsilon),
    )
  let built = pointsWithLengths(positions, indices, dirs, lengths)
  let parts = refresh(built)
  const phi = (Math.sqrt(5) - 1) / 2
  for (let pass = 0; pass < HANDLE_PASSES; pass += 1) {
    for (let at = 0; at < indices.length; at += 1) {
      const cap = caps[at]
      if (!(cap > 0)) continue
      const score = (length: number) => {
        lengths[at] = length
        built = pointsWithLengths(positions, indices, dirs, lengths)
        let energy = 0
        if (at > 0) {
          energy += segmentDeviation(positions, built, indices[at - 1], indices[at], at - 1, epsilon).energy
        }
        if (at + 1 < indices.length) {
          energy += segmentDeviation(positions, built, indices[at], indices[at + 1], at, epsilon).energy
        }
        return energy
      }
      let lo = 0
      let hi = cap
      const tol = Math.max(cap * 1e-4, 1e-6)
      let left = hi - phi * (hi - lo)
      let right = lo + phi * (hi - lo)
      let leftScore = score(left)
      let rightScore = score(right)
      while (hi - lo > tol) {
        if (leftScore <= rightScore) {
          hi = right
          right = left
          rightScore = leftScore
          left = hi - phi * (hi - lo)
          leftScore = score(left)
        } else {
          lo = left
          left = right
          leftScore = rightScore
          right = lo + phi * (hi - lo)
          rightScore = score(right)
        }
      }
      lengths[at] = leftScore <= rightScore ? left : right
      built = pointsWithLengths(positions, indices, dirs, lengths)
      parts = refresh(built)
    }
  }
  return { points: built, deviation: worstDeviation(parts) }
}

/** Ломаную записи — в точки кривой. Первая и последняя остаются, точек не больше 64. */
export function fitRecordedPoints(
  samples: readonly { pos: PathVec3 }[],
  epsilon = RECORDED_FIT_EPSILON_M,
): PathPoint[] {
  if (samples.length < 2) return []
  const positions = samples.map((sample) => sample.pos)
  const tolerance = Math.max(epsilon, 0)
  let indices = [0, positions.length - 1]
  let fitted = fitHandleLengths(positions, indices, tolerance)
  while (fitted.deviation.error > tolerance && indices.length < MAX_PATH_POINTS) {
    const insertAt = fitted.deviation.index
    if (indices.includes(insertAt)) break
    indices = indices.concat(insertAt).sort((a, b) => a - b)
    fitted = fitHandleLengths(positions, indices, tolerance)
  }
  // Лишние кадры, без которых кривая всё ещё в допуске, убираем.
  let removed = fitted.deviation.error <= tolerance
  while (removed && indices.length > 2) {
    removed = false
    for (let at = 1; at < indices.length - 1; at += 1) {
      const trial = indices.filter((_, index) => index !== at)
      const next = fitHandleLengths(positions, trial, tolerance)
      if (next.deviation.error <= tolerance) {
        indices = trial
        fitted = next
        removed = true
        break
      }
    }
  }
  return fitted.points
}

/** Запись WASD → кривая той же длительности и дорожка взгляда с кадров. */
export function fitRecordedFlight(
  samples: CameraPath["samples"],
  fov: number,
  epsilon = RECORDED_FIT_EPSILON_M,
): CameraPath {
  const points = fitRecordedPoints(samples, epsilon)
  const originalDuration = samples[samples.length - 1]?.t ?? 0
  const durationMs = clampSplineDuration(originalDuration > 0 ? originalDuration : DEFAULT_SPLINE_DURATION_MS)
  const speed = defaultFlySpeedProfile(durationMs)
  const baked = bakeSplinePath({ smooth: true, look: { mode: "recorded" }, points }, { durationMs, fov, speed })
  const keys = samples.map((sample) => {
    const fraction = originalDuration > 0 ? sample.t / originalDuration : 0
    return {
      t: clockAtArcFraction(speed, baked.duration_ms, fraction),
      yaw: sample.yaw,
      pitch: sample.pitch,
    }
  })
  return applyLookKeys(baked, keys)
}

/** Пролёт без точек становится кривой. Уже нарисованный путь не трогает. */
export function promoteRecordedPath(path: CameraPath, fov: number, epsilon = RECORDED_FIT_EPSILON_M): CameraPath {
  if ((path.authoring?.points.length ?? 0) > 0) return path
  if (path.samples.length < 2) return path
  return fitRecordedFlight(path.samples, fov, epsilon)
}

export function closestOnCurve(
  authoring: CameraPathAuthoring,
  origin: PathVec3,
  direction: PathVec3,
): { segment: number; pos: PathVec3; rayDistance: number } | null {
  if (authoring.points.length < 2) return null
  const dirLen = vecLen(direction)
  if (dirLen < 1e-8) return null
  const dir = scale(direction, 1 / dirLen)
  const poly = curvePolyline(authoring, 24)
  let best: { segment: number; pos: PathVec3; rayDistance: number } | null = null
  for (const sample of poly) {
    const toPoint = sub(sample.pos, origin)
    const along = dot(toPoint, dir)
    const closest = along <= 0 ? origin : add(origin, scale(dir, along))
    const rayDistance = vecLen(sub(sample.pos, closest))
    if (!best || rayDistance < best.rayDistance) {
      best = { segment: sample.segment, pos: sample.pos, rayDistance }
    }
  }
  return best
}

export function insertPoint(points: readonly PathPoint[], segment: number, pos: PathVec3): PathPoint[] | null {
  if (points.length >= MAX_PATH_POINTS) return null
  if (segment < 0 || segment >= points.length - 1) return null
  const next = points.map((point) => ({ pos: [...point.pos] as PathVec3, tangent: point.tangent }))
  next.splice(segment + 1, 0, { pos: [pos[0], pos[1], pos[2]], tangent: null })
  return next
}

export function deletePoint(points: readonly PathPoint[], index: number): PathPoint[] {
  return points
    .filter((_, i) => i !== index)
    .map((point) => ({ pos: [...point.pos] as PathVec3, tangent: point.tangent }))
}

export function resetPointTangent(points: readonly PathPoint[], index: number): PathPoint[] {
  return points.map((point, i) => ({
    pos: [...point.pos] as PathVec3,
    tangent: i === index ? null : point.tangent,
  }))
}

function cross(a: PathVec3, b: PathVec3): PathVec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

function unit(v: PathVec3): PathVec3 {
  const len = vecLen(v)
  if (len < 1e-8) return [0, 1, 0]
  return scale(v, 1 / len)
}

/** Смещение вдоль оси через плоскость, смотрящую на камеру. */
export function axisDragOffset(
  rayOrigin: PathVec3,
  rayDir: PathVec3,
  axisOrigin: PathVec3,
  axisDir: PathVec3,
  viewDir: PathVec3,
): number | null {
  const axis = unit(axisDir)
  const view = unit(viewDir)
  let normal = sub(view, scale(axis, dot(view, axis)))
  if (vecLen(normal) < 1e-4) {
    const up: PathVec3 = Math.abs(axis[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]
    normal = cross(axis, up)
  }
  normal = unit(normal)
  const denom = dot(rayDir, normal)
  if (Math.abs(denom) < 1e-8) return null
  const tRay = dot(sub(axisOrigin, rayOrigin), normal) / denom
  if (tRay < 0) return null
  const hit = add(rayOrigin, scale(rayDir, tRay))
  return dot(sub(hit, axisOrigin), axis)
}

/** Конец рычага едет по экрану. Глубина вдоль взгляда остаётся. */
export function handleDragTangent(
  pointPos: PathVec3,
  tangent: PathVec3,
  side: 1 | -1,
  rayOrigin: PathVec3,
  rayDir: PathVec3,
  viewDir: PathVec3,
): PathVec3 | null {
  const view = unit(viewDir)
  const tip = add(pointPos, scale(tangent, side))
  const denom = dot(rayDir, view)
  if (Math.abs(denom) < 1e-8) return null
  const tRay = dot(sub(tip, rayOrigin), view) / denom
  if (tRay < 0) return null
  const hit = add(rayOrigin, scale(rayDir, tRay))
  return clampTangent(scale(sub(hit, pointPos), side))
}
